import { EngagementBuyer } from '../models/engagement-buyer.model';
import { DocumentDetails } from '../models/document-details.model';
import { EngagementData } from '../models/engagement-data.model';
import { DocumentDetailsStatus } from '../models/enums/document-details-status.enum';
import { LoiSubmissionStatus } from '../models/enums/loi-submission-status.enum';
import { TimelineEventStatus } from '../models/enums/timeline-event-status.enum';
import { TimelineActionType } from '../models/timeline-action-type.model';
import { TimelineEvent } from '../models/timeline-event.model';
import { MarketplaceOpportunityStatus } from '../../opportunity/models/opportunity-status';
import {
    getActiveLoiProcess,
    getLastCompletedLoiProcess,
    LoiFormsProcessStep,
} from '../../loi-comparison/models/loi-forms-process.model';

// The fully executed LOI is the buyer's signable document of the LOI & DD NDA
// Execution event with status Completed — the status flips only when the
// seller signs it. The document is the buyer's action-state link on the
// execution action when one exists (Select & Execute prep flow — a signed DD
// NDA alone does not count there), otherwise this buyer's document generated
// from the event's template into the configured section — mirrors
// Timeline.GetBuyerDealMilestoneStatus on the API.
const hasExecutedLoiDocument = (
    buyer: EngagementBuyer,
    events: TimelineEvent[],
    engagementData: EngagementData
): boolean => {
    const loiExecutionActionIds = events
        .filter(
            (e) =>
                e.linkedAction?.actionType ===
                TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms
        )
        .map((e) => e.linkedAction.id);
    if (loiExecutionActionIds.length === 0) {
        return false;
    }

    const linkedLoiDocumentIds = (buyer.actionState ?? [])
        .filter(
            (acs) =>
                loiExecutionActionIds.includes(acs.actionId) &&
                acs.documentDetailsId
        )
        .map((acs) => acs.documentDetailsId);

    const documents = engagementData?.documentsList ?? [];
    const linkedLoiDocuments = documents.filter((document) =>
        linkedLoiDocumentIds.includes(document.id)
    );
    if (linkedLoiDocuments.length > 0) {
        return linkedLoiDocuments.some(
            (document) => document.status === DocumentDetailsStatus.Completed
        );
    }

    const executionConfigs =
        engagementData?.loiAndDdNdaExecutionConfigList ?? [];
    const isBuyerExecutionEventDocument = (document: DocumentDetails) =>
        document.engagementBuyerId === buyer.buyerId &&
        executionConfigs.some(
            (config) =>
                config.ddNdaSectionId != null &&
                document.sectionId === config.ddNdaSectionId &&
                document.templateId === config.ddNdaTemplateId
        );

    return documents.some(
        (document) =>
            document.status === DocumentDetailsStatus.Completed &&
            isBuyerExecutionEventDocument(document)
    );
};

// Rejected buyers never reach the Executed branch — after the seller finalizes
// the comparison, every non-selected buyer is rejected, so the one remaining
// buyer is the selection winner even if the isLoiSelected flag is not set.
const isSelectionWinner = (
    buyer: EngagementBuyer,
    events: TimelineEvent[]
): boolean =>
    buyer.isLoiSelected ||
    events.some(
        (e) =>
            e.linkedAction?.actionType ===
                TimelineActionType.SellerLoiComparison &&
            e.linkedAction.isSelectionFinalized
    );

export const deriveLoiStatus = (
    buyer: EngagementBuyer,
    events: TimelineEvent[],
    engagementData: EngagementData
): LoiSubmissionStatus | null => {
    const submissionEvent = events.find(
        (e) =>
            e.linkedAction?.actionType === TimelineActionType.BuyerLoiSubmission
    );
    if (
        !submissionEvent ||
        submissionEvent.status === TimelineEventStatus.Upcoming
    ) {
        return null;
    }

    const isRejected = buyer.actionState?.some(
        (acs) =>
            acs.buyerStatus === MarketplaceOpportunityStatus.RejectedByBuyer ||
            acs.buyerStatus === MarketplaceOpportunityStatus.RejectedBySeller
    );
    if (isRejected) {
        return LoiSubmissionStatus.Rejected;
    }

    // Selection alone does not flip the status — only the fully executed
    // (signed) LOI document does.
    if (
        isSelectionWinner(buyer, events) &&
        hasExecutedLoiDocument(buyer, events, engagementData)
    ) {
        return LoiSubmissionStatus.Executed;
    }

    const buyerProcesses = (engagementData?.loiFormsProcesses ?? []).filter(
        (process) => process.buyerId === buyer.buyerId
    );

    // A resubmission adds a new in-progress process next to the presented one —
    // the active process always wins over an earlier presented version.
    const activeProcess = getActiveLoiProcess(buyerProcesses);
    if (activeProcess) {
        if (activeProcess.step === LoiFormsProcessStep.BuyerSubmission) {
            return LoiSubmissionStatus.WaitingSubmission;
        }
        if (activeProcess.step === LoiFormsProcessStep.AdminReview) {
            const isSubmissionWindowOpen =
                submissionEvent.status === TimelineEventStatus.InProgress ||
                submissionEvent.status === TimelineEventStatus.Late;
            return isSubmissionWindowOpen
                ? LoiSubmissionStatus.Submitted
                : LoiSubmissionStatus.AdminReview;
        }
        // BuyerReview in progress — the buyer is adding comments to the detail
        // form, which the client counts as part of the admin-review window.
        return LoiSubmissionStatus.AdminReview;
    }

    const presentedProcess = getLastCompletedLoiProcess(buyerProcesses);
    if (!presentedProcess) {
        return LoiSubmissionStatus.WaitingSubmission;
    }

    return LoiSubmissionStatus.SellerReview;
};
